import {Link, useLocation} from "react-router-dom";
import React, {useEffect, useMemo, useState, useRef, useCallback} from "react";
import {findElementByLink} from "../utils/siteStructure";
import siteStructure from "../siteStructure.json";
import {Search} from "./Search";
import {useTheme} from "./Header";
import {
    HomeOutlined,
    SearchOutlined,
    MenuOutlined,
    CloseOutlined,
    InfoCircleOutlined,
    ShopOutlined,
    BuildOutlined,
    DollarOutlined,
    ContactsOutlined,
    WechatOutlined,
    NotificationOutlined,
    BookOutlined,
} from '@ant-design/icons';
import "../../css/mobileBottomBar.css";

export default function MobileBottomBar() {
    const { theme } = useTheme();

    const location = useLocation();
    const currentPath = location.pathname;
    const [searchState, setSearchState] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [dragOffset, setDragOffset] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const dragStartY = useRef(0);
    const startOffset = useRef(0);
    const menuContentRef = useRef(null);

    const openMenu = () => {
        setMenuOpen(true);
        setDragOffset(0);
    };
    const closeMenu = () => {
        setMenuOpen(false);
        setDragOffset(0);
        setIsDragging(false);
    };
    const toggleMenu = () => {
        if (menuOpen) closeMenu();
        else openMenu();
    };

    const handleLinkClick = () => {
        if (searchState) setSearchState(false);
        if (menuOpen) closeMenu();
    };

    const handleSearchClick = () => {
        if (menuOpen) closeMenu();
        setSearchState(prev => !prev);
    };
    const handleMenuClick = () => {
        if (searchState) setSearchState(false);
        toggleMenu();
    };

    // Функция для проверки активности ссылки
    const isActiveLink = (linkPath) => {
        if (linkPath.startsWith('http')) return false; // внешние ссылки не подсвечиваем
        // Для корневых путей: точное совпадение или вложенность (например, /about/contacts)
        const fullPath = `/${linkPath}`;
        if (currentPath === fullPath) return true;
        // Если текущий путь начинается с fullPath + '/', то это дочерний
        if (currentPath.startsWith(fullPath + '/')) return true;
        return false;
    };

    // Drag handlers
    const handleDragStart = (e) => {
        if (!menuOpen) return;
        e.preventDefault();
        setIsDragging(true);
        dragStartY.current = e.touches[0].clientY;
        startOffset.current = dragOffset;
        document.body.style.overflow = 'hidden';
        document.body.style.userSelect = 'none';
    };

    const handleDragMove = useCallback((e) => {
        if (!isDragging) return;
        e.preventDefault();
        const currentY = e.touches[0].clientY;
        const delta = currentY - dragStartY.current;
        let newOffset = startOffset.current + delta;
        const maxDrag = menuContentRef.current?.clientHeight || window.innerHeight;
        newOffset = Math.min(maxDrag, Math.max(0, newOffset));
        setDragOffset(newOffset);
    }, [isDragging, dragStartY, startOffset]); // dragStartY и startOffset – refs, они стабильны

    const handleDragEnd = useCallback(() => {
        if (!isDragging) return;
        setIsDragging(false);
        document.body.style.overflow = '';
        document.body.style.userSelect = '';
        const threshold = Math.min(80, (menuContentRef.current?.clientHeight || 400) * 0.2);
        if (dragOffset > threshold) {
            closeMenu();
        } else {
            setDragOffset(0);
        }
    }, [isDragging, dragOffset]); // closeMenu стабильна, dragOffset – зависимость

    useEffect(() => {
        if (!menuOpen) return;
        const handleTouchMove = (e) => {
            if (isDragging) handleDragMove(e);
        };
        const handleTouchEnd = () => {
            if (isDragging) handleDragEnd();
        };
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', handleTouchEnd);
        return () => {
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
        };
    }, [menuOpen, isDragging, handleDragMove, handleDragEnd]);

    useEffect(() => {
        document.body.style.overflow = searchState ? 'hidden' : 'auto';
        document.body.style.height = searchState ? '100vh' : 'auto';
        return () => {
            document.body.style.overflow = 'auto';
            document.body.style.height = 'auto';
        };
    }, [searchState]);

    const menuLinks = useMemo(() => {
        const linksData = [
            { path: 'about' },
            { path: 'services' },
            { path: 'production', hasMegaMenu: true },
            { path: 'lizing' },
            { path: 'about/contacts' },
            { path: 'vacancy', label: 'Вакансии' },
            { path: 'knowledge-base', label: 'База знаний' },
            { path: 'https://forum.sespel.su/posts/recent', label: 'Форум' }
        ];
        linksData.forEach((item, i) => {
            const menuData = findElementByLink(siteStructure, item.path, true);
            if (menuData) {
                linksData[i].label = menuData.title;
                if (menuData.links) linksData[i].links = menuData.links;
            }
        });
        return linksData;
    }, []);

    const getIcon = (path) => {
        switch (path) {
            case 'about': return <InfoCircleOutlined />;
            case 'services': return <ShopOutlined />;
            case 'production': return <BuildOutlined />;
            case 'lizing': return <DollarOutlined />;
            case 'about/contacts': return <ContactsOutlined />;
            case 'news': return <NotificationOutlined />;
            case 'knowledge-base': return <BookOutlined />;
            default: return <WechatOutlined />;
        }
    };

    return (
        <>
            <div className={`mobileBottomBar ${theme}`}>
                <Link to="/" className={`bottomBarButton ${currentPath === '/' ? 'active' : ''}`} onClick={handleLinkClick}>
                    <HomeOutlined />
                    <span>Главная</span>
                </Link>
                <Link to="/news" className={`bottomBarButton ${currentPath === '/news' ? 'active' : ''}`} onClick={handleLinkClick}>
                    <NotificationOutlined />
                    <span>Новости</span>
                </Link>
                <button className="bottomBarButton" onClick={handleSearchClick}>
                    {searchState ? <CloseOutlined /> : <SearchOutlined />}
                    <span>{searchState ? 'Закрыть' : 'Поиск'}</span>
                </button>
                <button className="bottomBarButton" onClick={handleMenuClick}>
                    <MenuOutlined />
                    <span>Меню</span>
                </button>
            </div>

            <Search searchState={searchState} setSearchState={setSearchState} />

            <div className={`mobileMenuOverlay ${theme} ${menuOpen ? 'open' : ''}`} onClick={closeMenu}>
                <div
                    className="mobileMenu"
                    ref={menuContentRef}
                    onClick={e => e.stopPropagation()}
                    onTouchStart={handleDragStart}
                    style={
                        menuOpen && (isDragging || dragOffset !== 0)
                            ? { transform: `translateY(${dragOffset}px)` }
                            : undefined
                    }
                >
                    <div className={`mobileMenuHeader ${theme}`}>
                        <h3>Меню</h3>
                        <CloseOutlined className="close-menu-icon" onClick={closeMenu} />
                    </div>
                    <ul className={`mobileMenuList ${theme}`}>
                        {menuLinks.map((link, idx) => {
                            const isExternal = link.path.startsWith('http');
                            const active = !isExternal && isActiveLink(link.path);
                            return (
                                <li key={idx}>
                                    {isExternal ? (
                                        <a href={link.path} target="_blank" rel="noopener noreferrer" onClick={closeMenu} className={active ? 'active' : ''}>
                                            {getIcon(link.path)}
                                            <span>{link.label}</span>
                                        </a>
                                    ) : (
                                        <Link to={`/${link.path}`} onClick={closeMenu} className={active ? 'active' : ''}>
                                            {getIcon(link.path)}
                                            <span>{link.label}</span>
                                        </Link>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </>
    );
}
